// @flow

import * as React from 'react';
import {useState, useContext, useEffect} from 'react';
import {useStoreState} from 'react-flow-renderer';
import {contextStubFunction} from 'docs/util';
import {every, cloneDeep} from 'lodash';
import {useSequenceDiagram} from 'docs/data/diagram/sequence-diagram/SequenceDiagramProvider';
import {
  buildOverviewDiagram,
  buildSequenceDiagram,
  buildStateDiagram,
  buildEntityDiagram,
  positionSequenceElements,
  hasDimensions,
  getMaxHeight,
  getSegmentWidth,
} from 'docs/components/core/Diagram/util';
import type {
  DiagramType,
  Element,
  Edge,
} from 'docs/components/core/Diagram/types';
import {diagramMarkdoc} from './diagramMarkdoc';
import {mockNode} from './mockNode';
import {mockEdge} from './mockEdge';
import type {MockNodeAttributes} from './mockNode';
import type {MockEdgeData} from './mockEdge';

const BUILD = {
  overview: buildOverviewDiagram,
  state: buildStateDiagram,
  sequence: buildSequenceDiagram,
  entity: buildEntityDiagram,
};

export type DiagramGeneratorContextProps = {
  type: DiagramType,
  elements: Array<Element>,
  markdoc: string,
  showHandles: boolean,
  showNodeModal: boolean,
  showEdgeModal: boolean,
  setShowHandles: (boolean) => void,
  setShowNodeModal: (boolean) => void,
  setShowEdgeModal: (boolean) => void,
  setType: (DiagramType) => void,
  setDescription: (string) => void,
  addNode: (MockNodeAttributes, string | Array<any>) => void,
  addEdge: (Edge) => void,
  addEdgeContent: (MockEdgeData) => void,
  reset: () => void,
  onNodeDragStop: (any) => void,
};

const DiagramGeneratorContext = React.createContext<DiagramGeneratorContextProps>(
  {
    type: 'overview',
    elements: [],
    markdoc: '',
    showHandles: true,
    showNodeModal: false,
    showEdgeModal: false,
    setShowHandles: contextStubFunction('DiagramGenerator'),
    setShowNodeModal: contextStubFunction('DiagramGenerator'),
    setShowEdgeModal: contextStubFunction('DiagramGenerator'),
    setType: contextStubFunction('DiagramGenerator'),
    setDescription: contextStubFunction('DiagramGenerator'),
    addNode: contextStubFunction('DiagramGenerator'),
    addEdgeContent: contextStubFunction('DiagramGenerator'),
    addEdge: contextStubFunction('DiagramGenerator'),
    reset: contextStubFunction('DiagramGenerator'),
    onNodeDragStop: contextStubFunction('DiagramGenerator'),
  },
);

type DiagramGeneratorProviderProps = {|
  initialType: DiagramType,
  setLoading: (boolean) => void,
  children: React.Node,
|};

export const DiagramGeneratorProvider = ({
  initialType,
  setLoading,
  children,
}: DiagramGeneratorProviderProps) => {
  // Internal state provided by react-flow-renderer to let us know the dimensions of the rendered nodes
  const diagramNodes = useStoreState((store) => store.nodes);
  const diagramEdges = useStoreState((store) => store.edges);

  const {setHeight, setSegmentWidth} = useSequenceDiagram();

  // State to keep track of the nodes and edges the user is creating
  const [type, setType] = useState(initialType);
  const [description, setDescription] = useState('');
  const [elements, setElements] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [edge, setEdge] = useState({
    from: '',
    to: '',
    fromPosition: '',
    toPosition: '',
  });
  const [markdoc, setMarkdoc] = useState(
    diagramMarkdoc(type, description, [], []),
  );

  // UI helpers
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [showEdgeModal, setShowEdgeModal] = useState(false);
  const [buildGraph, setBuildGraph] = useState(true);
  const [showHandles, setShowHandles] = useState(true);

  useEffect(() => {
    // only build the graph if the nodes have been rendered on the page
    // so we know their height and width before calculating their position
    if (
      buildGraph &&
      diagramNodes?.length + diagramEdges?.length === elements.length &&
      every(diagramNodes, hasDimensions)
    ) {
      let positionedElements;
      if (type === 'sequence') {
        const maxHeight = getMaxHeight(diagramNodes);
        const segmentWidth = getSegmentWidth(diagramNodes);
        positionedElements = positionSequenceElements(
          elements,
          maxHeight,
          segmentWidth,
        );
        setHeight(maxHeight);
        setSegmentWidth(segmentWidth);
      } else {
        // Elements are manually positioned
        positionedElements = elements;
      }

      setBuildGraph(false);
      setElements(positionedElements);
      setLoading(false);
    }
  }, [
    diagramNodes,
    diagramEdges,
    elements,
    buildGraph,
    type,
    setLoading,
    setHeight,
    setSegmentWidth,
  ]);

  useEffect(() => {
    const build = BUILD[type];
    if (build) {
      // Construct the set of elements to render into the diagram
      const els = build({type}, [...nodes, ...edges]);
      setElements(els);
      setBuildGraph(true);
      // Update the sample Markdoc shown
      setMarkdoc(diagramMarkdoc(type, description, nodes, edges));
    }
  }, [nodes, edges, description, type]);

  // Adds a new node to the diagram
  // {id, text, state, icon, handles}
  const addNode = (
    attributes: MockNodeAttributes,
    content: string | Array<any>,
  ) => {
    const n = mockNode({...attributes, x: 0, y: 0}, content);
    setNodes([...nodes, n]);
    setShowNodeModal(false);
  };

  // Takes any content the user entered and constructs
  // a new edge with the content
  const addEdgeContent = (data: MockEdgeData) => {
    const e = mockEdge(edge, data);
    setEdges([...edges, e]);
    setShowEdgeModal(false);
  };

  // Adds a new edge to the diagram and prompts the user
  // to add a label to the edge if needed
  const addEdge = ({source, target, sourceHandle, targetHandle}: Edge) => {
    if (source === target && type !== 'sequence') {
      // Don't draw edges that connect to the same node unless its a sequence diagram
      return;
    }
    const newEdge = {
      from: source,
      to: target,
      fromPosition: sourceHandle || '',
      toPosition: targetHandle || '',
    };
    setEdge(newEdge);
    setShowEdgeModal(true);
  };

  // Resets the diagram
  const reset = () => {
    setNodes([]);
    setEdges([]);
  };

  // Sets the x/y positions on the dragged node
  const onNodeDragStop = (event: any, node: any) => {
    const draggedNode = nodes.find((n) => n.attributes.id === node.id);
    const index = nodes.indexOf(draggedNode);
    if (draggedNode) {
      const positionedNode = cloneDeep(draggedNode);
      positionedNode.attributes.x = Math.round(node.position.x);
      positionedNode.attributes.y = Math.round(node.position.y);

      const updatedNodes = [
        ...nodes.slice(0, index),
        positionedNode,
        ...nodes.slice(index + 1, nodes.length),
      ];
      setNodes(updatedNodes);
    }
  };

  const value = {
    type,
    elements,
    markdoc,
    showHandles,
    showNodeModal,
    showEdgeModal,
    setShowNodeModal,
    setShowEdgeModal,
    setShowHandles,
    setType,
    setDescription,
    addNode,
    addEdge,
    addEdgeContent,
    reset,
    onNodeDragStop,
  };

  return (
    <DiagramGeneratorContext.Provider value={value}>
      {children}
    </DiagramGeneratorContext.Provider>
  );
};

export const useDiagramGenerator = () => {
  return useContext(DiagramGeneratorContext);
};
